import React, { useState, useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../../../helpers/config';

type Status = 'loading' | 'success' | 'error';

const UnsubscribeConfirmation: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState<string>('');
  const hasCalledApi = useRef(false);

  const API_BASE_URL = getApiBaseUrl();

  const getToken = (): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  };

  const confirmUnsubscribe = async (): Promise<void> => {
    // Prevent double calls
    if (hasCalledApi.current) 
      return;
    hasCalledApi.current = true;

    const token = getToken();

    if (!token) {
      setErrorMsg('No unsubscribe token found. Please check your email link.');
      return;
    }

    try {
      handleResponse(await callUnsubscribeApi(token));
    } catch (error) {
      handleApiError();
    }
  };

  const callUnsubscribeApi = async (token: string): Promise<Response> => {
    return fetch(`${API_BASE_URL}/api/unsubscribe?token=${token}`);
  }

  const setErrorMsg = (msg: string): void => {
    setStatus('error');
    setMessage(msg);
  };

  const setSuccessMsg = (msg: string): void => {
    setStatus('success');
    setMessage(msg);
  };

  const handleApiError = (): void => {
    setErrorMsg('Oops, something went wrong. Please try again.');
  };

  const handleResponse = (response: Response): void => {
    if (response.ok) {
      setSuccessMsg('You have been successfully unsubscribed. We\'re sorry to see you go!');
    } else {
      handleApiError();
    }
  };

  useEffect(() => {
    confirmUnsubscribe();
  }, []);

  interface StatusViewProps {
    status: Status;
    message: string;
  }

  function LoadingView() {
    return (
      <>
        <div className="border-4 border-gray-200 border-t-blue-500 rounded-full w-12 h-12 animate-spin mx-auto mb-5"></div>
        <h2 className="text-3xl mb-4 text-gray-800">Processing your request...</h2>
        <p className="text-base text-gray-600 mb-5 leading-relaxed">Please wait a moment.</p>
      </>
    );
  }

  function SuccessView({ message }: { message: string }) {
    return (
      <>
        <div className="text-6xl text-green-500 mb-5">✓</div>
        <h2 className="text-3xl mb-4 text-gray-800">You're Unsubscribed</h2>
        <p className="text-base text-gray-600 mb-5 leading-relaxed">{message}</p>
        <p className="text-sm text-gray-400 mb-8 leading-relaxed">
          You will no longer receive emails from us. If you change your mind, 
          you can always sign up again.
        </p>
        <ActionButton href="/" label="Go to Homepage" />
      </>
    );
  }

  function ErrorView({ message }: { message: string }) {
    return (
      <>
        <div className="text-6xl text-red-500 mb-5">✕</div>
        <h2 className="text-3xl mb-4 text-gray-800">Something Went Wrong</h2>
        <p className="text-base text-gray-600 mb-5 leading-relaxed">{message}</p>
        <ActionButton href="/contact" label="Contact Support" />
      </>
    );
}

  function ActionButton({ href, label }: { href: string; label: string }) {
    return (
      <a 
        href={href}
        className="inline-block py-3 px-8 bg-blue-500 text-white no-underline rounded text-base transition-colors hover:bg-blue-600"
      >
        {label}
      </a>
    );
  }

  function UnsubscribeStatus({ status, message }: StatusViewProps) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-100 p-5">
        <div className="bg-white rounded-lg p-10 max-w-lg w-full text-center shadow-lg">
          {status === 'loading' && <LoadingView />}
          {status === 'success' && <SuccessView message={message} />}
          {status === 'error' && <ErrorView message={message} />}
        </div>
      </div>
    );
  }

  return <UnsubscribeStatus status={status} message={message} />;
};

export default UnsubscribeConfirmation;